import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { User } from '../types';
import api from '../api/client';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => void;
  register: () => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently
  } = useAuth0();
  
  const [user, setUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState(false);
  const [initialAuthCheck, setInitialAuthCheck] = useState(true);

  const refreshUser = useCallback(async () => {
    if (isAuthenticated && auth0User) {
      setUserLoading(true);
      try {
        const token = await getAccessTokenSilently();
        
        // Set token in API client
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Fetch user from our backend
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error: any) {
        console.error('Failed to load user:', error);
        // Clear stale token if auth fails (e.g., missing refresh token)
        localStorage.removeItem('access_token');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      } finally {
        setUserLoading(false);
      }
    } else {
      setUser(null);
      setUserLoading(false);
    }
  }, [isAuthenticated, auth0User, getAccessTokenSilently]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Mark initial auth check as complete once auth0 has loaded at least once
  // Small delay prevents race condition where Auth0 reports isLoading=false before isAuthenticated updates
  useEffect(() => {
    if (!auth0Loading && initialAuthCheck) {
      const timer = setTimeout(() => {
        setInitialAuthCheck(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [auth0Loading, initialAuthCheck]);

  // Combined loading state: wait for both Auth0 and our backend
  const loading = auth0Loading || userLoading || initialAuthCheck;

  const login = useCallback(() => {
    // Don't return to logged-out or verify-email pages after login
    const currentPath = window.location.pathname;
    const excludedPaths = ['/logged-out', '/verify-email'];
    const returnTo = excludedPaths.includes(currentPath) ? '/' : currentPath + window.location.search;

    loginWithRedirect({
      appState: {
        returnTo
      }
    });
  }, [loginWithRedirect]);

  const register = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup'
      },
      appState: {
        returnTo: window.location.pathname + window.location.search
      }
    }).catch((error) => {
      console.error('Registration error:', error);
      // Auth0 handles the error UI, but we log it here
      if (error.error === 'invalid_user_password' || error.error_description?.includes('already exists')) {
        console.log('User already exists - they should log in instead');
      }
    });
  }, [loginWithRedirect]);

  const logout = useCallback(() => {
    setUser(null);
    // Clear API auth header
    delete api.defaults.headers.common['Authorization'];
    auth0Logout({ 
      logoutParams: { 
        returnTo: `${window.location.origin}/logged-out`,
        federated: true
      } 
    });
  }, [auth0Logout]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
